import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  TextField,
  Button,
  Grid,
  InputAdornment,
  Popover,
  Typography,
  Divider,
  IconButton,
} from '@mui/material';
import { Autocomplete, Popper } from '@mui/material';
import {
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Add,
  Remove,
  MeetingRoom,
  ChildCare,
} from '@mui/icons-material';

// ============================================================================
// STEPPER INPUT COMPONENT
// ============================================================================

interface StepperInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  label: string;
  sublabel?: string;
  icon?: React.ReactNode;
}

const StepperInput: React.FC<StepperInputProps> = ({
  value,
  onChange,
  min = 0,
  max = 10,
  label,
  sublabel,
  icon,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      py: 1.5,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        {sublabel && (
          <Typography variant="caption" color="text.secondary">
            {sublabel}
          </Typography>
        )}
      </Box>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <IconButton
        size="small"
        onClick={() => value > min && onChange(value - 1)}
        disabled={value <= min}
        sx={{
          border: '1px solid',
          borderColor: value <= min ? '#e0e0e0' : '#2D6E6A',
          color: value <= min ? '#e0e0e0' : '#2D6E6A',
          width: 32,
          height: 32,
        }}
      >
        <Remove fontSize="small" />
      </IconButton>
      <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 600 }}>
        {value}
      </Typography>
      <IconButton
        size="small"
        onClick={() => value < max && onChange(value + 1)}
        disabled={value >= max}
        sx={{
          border: '1px solid',
          borderColor: value >= max ? '#e0e0e0' : '#2D6E6A',
          color: value >= max ? '#e0e0e0' : '#2D6E6A',
          width: 32,
          height: 32,
        }}
      >
        <Add fontSize="small" />
      </IconButton>
    </Box>
  </Box>
);

// ============================================================================
// TYPES
// ============================================================================

interface HotelSearchFormProps {
  onSearch?: (params: HotelSearchParams) => void;
}

interface HotelSearchParams {
  destination: string;
  checkIn: string;
  checkOut: string;
  rooms: number;
  adults: number;
  children: number;
}
// ============================================================================
// DESTINATION DATA & AUTOCOMPLETE
// ============================================================================

interface DestinationOption {
  name: string;
  region: string;
  country: string;
  type: 'city' | 'region' | 'landmark';
  label: string;
}

const DESTINATIONS: DestinationOption[] = [
  // US Cities
  { name: 'New York', region: 'New York', country: 'US', type: 'city', label: 'New York, NY, USA' },
  { name: 'Manhattan', region: 'New York', country: 'US', type: 'region', label: 'Manhattan, New York, USA' },
  { name: 'Los Angeles', region: 'California', country: 'US', type: 'city', label: 'Los Angeles, CA, USA' },
  { name: 'San Francisco', region: 'California', country: 'US', type: 'city', label: 'San Francisco, CA, USA' },
  { name: 'Chicago', region: 'Illinois', country: 'US', type: 'city', label: 'Chicago, IL, USA' },
  { name: 'Miami', region: 'Florida', country: 'US', type: 'city', label: 'Miami, FL, USA' },
  { name: 'Miami Beach', region: 'Florida', country: 'US', type: 'region', label: 'Miami Beach, FL, USA' },
  { name: 'South Beach', region: 'Florida', country: 'US', type: 'region', label: 'South Beach, Miami, FL, USA' },
  { name: 'Orlando', region: 'Florida', country: 'US', type: 'city', label: 'Orlando, FL, USA' },
  { name: 'Las Vegas', region: 'Nevada', country: 'US', type: 'city', label: 'Las Vegas, NV, USA' },
  { name: 'The Strip', region: 'Nevada', country: 'US', type: 'landmark', label: 'The Strip, Las Vegas, NV, USA' },
  { name: 'Seattle', region: 'Washington', country: 'US', type: 'city', label: 'Seattle, WA, USA' },
  { name: 'Boston', region: 'Massachusetts', country: 'US', type: 'city', label: 'Boston, MA, USA' },
  { name: 'Washington', region: 'District of Columbia', country: 'US', type: 'city', label: 'Washington, DC, USA' },
  { name: 'San Diego', region: 'California', country: 'US', type: 'city', label: 'San Diego, CA, USA' },
  { name: 'Denver', region: 'Colorado', country: 'US', type: 'city', label: 'Denver, CO, USA' },
  { name: 'Austin', region: 'Texas', country: 'US', type: 'city', label: 'Austin, TX, USA' },
  { name: 'Nashville', region: 'Tennessee', country: 'US', type: 'city', label: 'Nashville, TN, USA' },
  { name: 'New Orleans', region: 'Louisiana', country: 'US', type: 'city', label: 'New Orleans, LA, USA' },
  { name: 'Honolulu', region: 'Hawaii', country: 'US', type: 'city', label: 'Honolulu, HI, USA' },
  { name: 'Waikiki', region: 'Hawaii', country: 'US', type: 'region', label: 'Waikiki, Honolulu, HI, USA' },
  // Europe
  { name: 'London', region: 'England', country: 'UK', type: 'city', label: 'London, England, UK' },
  { name: 'Paris', region: 'Île-de-France', country: 'France', type: 'city', label: 'Paris, France' },
  { name: 'Rome', region: 'Lazio', country: 'Italy', type: 'city', label: 'Rome, Italy' },
  { name: 'Barcelona', region: 'Catalonia', country: 'Spain', type: 'city', label: 'Barcelona, Spain' },
  { name: 'Madrid', region: 'Community of Madrid', country: 'Spain', type: 'city', label: 'Madrid, Spain' },
  { name: 'Amsterdam', region: 'North Holland', country: 'Netherlands', type: 'city', label: 'Amsterdam, Netherlands' },
  { name: 'Berlin', region: 'Berlin', country: 'Germany', type: 'city', label: 'Berlin, Germany' },
  { name: 'Munich', region: 'Bavaria', country: 'Germany', type: 'city', label: 'Munich, Germany' },
  { name: 'Vienna', region: 'Vienna', country: 'Austria', type: 'city', label: 'Vienna, Austria' },
  { name: 'Prague', region: 'Bohemia', country: 'Czech Republic', type: 'city', label: 'Prague, Czech Republic' },
  { name: 'Dublin', region: 'Leinster', country: 'Ireland', type: 'city', label: 'Dublin, Ireland' },
  { name: 'Lisbon', region: 'Lisbon', country: 'Portugal', type: 'city', label: 'Lisbon, Portugal' },
  // Asia & Pacific
  { name: 'Tokyo', region: 'Kanto', country: 'Japan', type: 'city', label: 'Tokyo, Japan' },
  { name: 'Kyoto', region: 'Kansai', country: 'Japan', type: 'city', label: 'Kyoto, Japan' },
  { name: 'Singapore', region: 'Singapore', country: 'Singapore', type: 'city', label: 'Singapore' },
  { name: 'Bangkok', region: 'Central Thailand', country: 'Thailand', type: 'city', label: 'Bangkok, Thailand' },
  { name: 'Hong Kong', region: 'Hong Kong', country: 'China', type: 'city', label: 'Hong Kong, China' },
  { name: 'Seoul', region: 'Seoul', country: 'South Korea', type: 'city', label: 'Seoul, South Korea' },
  { name: 'Sydney', region: 'New South Wales', country: 'Australia', type: 'city', label: 'Sydney, Australia' },
  { name: 'Melbourne', region: 'Victoria', country: 'Australia', type: 'city', label: 'Melbourne, Australia' },
  { name: 'Bali', region: 'Bali', country: 'Indonesia', type: 'region', label: 'Bali, Indonesia' },
  { name: 'Dubai', region: 'Dubai', country: 'UAE', type: 'city', label: 'Dubai, UAE' },
  // Caribbean & Mexico
  { name: 'Cancún', region: 'Quintana Roo', country: 'Mexico', type: 'city', label: 'Cancún, Mexico' },
  { name: 'Riviera Maya', region: 'Quintana Roo', country: 'Mexico', type: 'region', label: 'Riviera Maya, Mexico' },
  { name: 'Mexico City', region: 'CDMX', country: 'Mexico', type: 'city', label: 'Mexico City, Mexico' },
  { name: 'San Juan', region: 'Puerto Rico', country: 'US', type: 'city', label: 'San Juan, Puerto Rico' },
  { name: 'Nassau', region: 'New Providence', country: 'Bahamas', type: 'city', label: 'Nassau, Bahamas' },
  { name: 'Punta Cana', region: 'La Altagracia', country: 'Dominican Republic', type: 'city', label: 'Punta Cana, Dominican Republic' },
  { name: 'Montego Bay', region: 'St. James', country: 'Jamaica', type: 'city', label: 'Montego Bay, Jamaica' },
  // Canada
  { name: 'Toronto', region: 'Ontario', country: 'Canada', type: 'city', label: 'Toronto, Ontario, Canada' },
  { name: 'Vancouver', region: 'British Columbia', country: 'Canada', type: 'city', label: 'Vancouver, BC, Canada' },
  { name: 'Montreal', region: 'Quebec', country: 'Canada', type: 'city', label: 'Montreal, Quebec, Canada' },
];

const filterDestinations = (query: string): DestinationOption[] => {
  if (!query || query.length < 2) return [];
  const normalizedQuery = query.toLowerCase().trim();
  
  return DESTINATIONS.filter(dest => {
    const searchFields = [
      dest.name.toLowerCase(),
      dest.region.toLowerCase(),
      dest.country.toLowerCase(),
      dest.label.toLowerCase(),
    ];
    return searchFields.some(field => field.includes(normalizedQuery));
  }).slice(0, 8);
};

const AutocompletePopper = (props: any) => (
  <Popper {...props} placement="bottom-start" style={{ zIndex: 1500 }} />
);
// ============================================================================
// MAIN COMPONENT
// ============================================================================

const HotelSearchForm: React.FC<HotelSearchFormProps> = ({ onSearch }) => {
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];

  const [formData, setFormData] = useState<HotelSearchParams>({
    destination: '',
    checkIn: today,
    checkOut: tomorrow,
    rooms: 1,
    adults: 2,
    children: 0,
  });
  const [dateError, setDateError] = useState('');

  // Guest selector popover
  const [guestAnchor, setGuestAnchor] = useState<HTMLElement | null>(null);

  const handleChange = (field: keyof HotelSearchParams) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDateError('');
    const newData = { ...formData, [field]: e.target.value };
    
    // Auto-correct: if check-in >= check-out, push check-out to next day
    if (field === 'checkIn' && e.target.value >= newData.checkOut) {
      const nextDay = new Date(e.target.value);
      nextDay.setDate(nextDay.getDate() + 1);
      newData.checkOut = nextDay.toISOString().split('T')[0];
    }
    
    // Prevent check-out from being on or before check-in
    if (field === 'checkOut' && e.target.value <= newData.checkIn) {
      setDateError('Check-out must be after check-in date');
      return;
    }
    
    setFormData(newData);
  };

  const handleSearch = () => {
    if (!formData.destination.trim()) {
      setDateError('Please enter a destination');
      return;
    }
    if (formData.checkOut <= formData.checkIn) {
      setDateError('Check-out must be after check-in');
      return;
    }
    setDateError('');
    if (onSearch) {
      onSearch(formData);
    } else {
      // Navigate to search results page
      const params = new URLSearchParams({
        destination: formData.destination,
        checkIn: formData.checkIn,
        checkOut: formData.checkOut,
        rooms: formData.rooms.toString(),
        adults: formData.adults.toString(),
        children: formData.children.toString(),
      });
      navigate(`/search/hotels?${params.toString()}`);
    }
  };

  // Guest summary text
  const guestSummary = `${formData.rooms} room${formData.rooms > 1 ? 's' : ''}, ${formData.adults} adult${formData.adults > 1 ? 's' : ''}${formData.children > 0 ? `, ${formData.children} child${formData.children > 1 ? 'ren' : ''}` : ''}`;

  // Check-out min date (day after check-in)
  const checkOutMin = formData.checkIn
    ? new Date(new Date(formData.checkIn).getTime() + 86400000).toISOString().split('T')[0]
    : tomorrow;

    return (
      <Box>
        {dateError && (
          <Alert severity="error" onClose={() => setDateError('')} sx={{ mb: 1.5, borderRadius: 2 }}>
            {dateError}
          </Alert>
        )}
        <Grid container spacing={2} alignItems="flex-end">
       {/* Destination */}
       <Grid item xs={12} md={3}>
          <Autocomplete
            freeSolo
            options={filterDestinations(formData.destination)}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
            inputValue={formData.destination}
            onInputChange={(_, newValue) => setFormData({ ...formData, destination: newValue })}
            onChange={(_, newValue) => {
              if (newValue && typeof newValue !== 'string') {
                setFormData({ ...formData, destination: newValue.label });
              }
            }}
            PopperComponent={AutocompletePopper}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <Box sx={{ 
                    bgcolor: option.type === 'city' ? '#E8F5E9' : option.type === 'region' ? '#E3F2FD' : '#FFF3E0',
                    color: option.type === 'city' ? '#2E7D32' : option.type === 'region' ? '#1976D2' : '#E65100',
                    px: 0.75, 
                    py: 0.25, 
                    borderRadius: 1,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                  }}>
                    {option.type}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.region}, {option.country}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="Where are you going?"
                placeholder="City or destination"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <LocationIcon color="primary" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            )}
          />
        </Grid>

        {/* Check-in */}
        <Grid item xs={6} md={2}>
          <TextField
            fullWidth
            label="Check-in"
            type="date"
            value={formData.checkIn}
            onChange={handleChange('checkIn')}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: today }}
            
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </Grid>

       {/* Check-out */}
       <Grid item xs={6} md={2}>
          <TextField
            fullWidth
            label="Check-out"
            type="date"
            value={formData.checkOut}
            onChange={handleChange('checkOut')}
            onFocus={(e) => {
              const input = e.target as HTMLInputElement;
              input.min = checkOutMin;
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: checkOutMin }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </Grid>

        {/* Guests */}
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Guests"
            value={guestSummary}
            onClick={(e) => setGuestAnchor(e.currentTarget)}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                cursor: 'pointer',
              },
              '& .MuiOutlinedInput-input': {
                cursor: 'pointer',
              },
            }}
          />

          {/* Guest Selector Popover */}
          <Popover
            open={Boolean(guestAnchor)}
            anchorEl={guestAnchor}
            onClose={() => setGuestAnchor(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                p: 2,
                width: 300,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              },
            }}
          >
            <StepperInput
              value={formData.rooms}
              onChange={(val) => setFormData({ ...formData, rooms: val })}
              min={1}
              max={9}
              label="Rooms"
              icon={<MeetingRoom sx={{ color: '#666' }} />}
            />
            <Divider sx={{ my: 1 }} />
            <StepperInput
              value={formData.adults}
              onChange={(val) => setFormData({ ...formData, adults: val })}
              min={1}
              max={30}
              label="Adults"
              sublabel="Age 18+"
              icon={<PersonIcon sx={{ color: '#666' }} />}
            />
            <Divider sx={{ my: 1 }} />
            <StepperInput
              value={formData.children}
              onChange={(val) => setFormData({ ...formData, children: val })}
              min={0}
              max={10}
              label="Children"
              sublabel="Age 0-17"
              icon={<ChildCare sx={{ color: '#666' }} />}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={() => setGuestAnchor(null)}
              sx={{
                mt: 2,
                bgcolor: '#2D6E6A',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: '#009688' },
              }}
            >
              Done
            </Button>
          </Popover>
        </Grid>

        {/* Search Button */}
        <Grid item xs={12} md={2}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleSearch}
            disabled={!formData.destination || !formData.checkIn || !formData.checkOut || formData.checkOut <= formData.checkIn}
            startIcon={<SearchIcon />}
            sx={{
              py: 1.8,
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            }}
          >
            Search Hotels
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HotelSearchForm;
